import { useApi, useSilentApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import { DEFAULT_VM_MAX_RUNTIME_SECONDS } from '@/stores/type/calculation.type'
import type {
  Calculation,
  CalculationComputeResourceLogsResponse,
  CalculationFormPayload,
  CalculationReschedulePayload,
  ContainerImageOption,
  CalculationComputeResource,
  CalculationResultFile,
  CalculationResultFilesResponse,
  CalculationStorageCost
} from '@/stores/type/calculation.type'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { CalculationProgressInfo } from '@/components/calculation-result/type/type'
import { useSelectedItemMixin } from '@/stores/useSelectedItemMixin'

const { api } = useApi()
const { api: silentApi } = useSilentApi()

const baseUrl = API.CORE.CALCULATIONS.INDEX
const containerImagesUrl = API.CORE.CALCULATIONS.CONTAINER_IMAGES
const getCeleryCalculateUrl = API.CORE.CALCULATIONS.CELERY_CALCULATE
const getRescheduleUrl = API.CORE.CALCULATIONS.RESCHEDULE
const getStopUrl = API.CORE.CALCULATIONS.STOP
const getComputeResourcesUrl = API.CORE.CALCULATIONS.GET_COMPUTE_RESOURCES
const getHistoricalComputeResourcesUrl = API.CORE.CALCULATIONS.GET_HISTORICAL_COMPUTE_RESOURCES
const getResultFilesUrl = API.CORE.CALCULATIONS.GET_RESULT_FILES
const getComputeResourceLogsUrl = API.CORE.CALCULATIONS.GET_COMPUTE_RESOURCE_LOGS
const getDownloadComputeResourceLogsUrl = API.CORE.CALCULATIONS.DOWNLOAD_COMPUTE_RESOURCE_LOGS
const getDownloadResultFileUrl = API.CORE.CALCULATIONS.DOWNLOAD_RESULT_FILE
const getDeleteResultFileUrl = API.CORE.CALCULATIONS.DELETE_RESULT_FILE
const getDownloadResultFilesZipUrl = API.CORE.CALCULATIONS.DOWNLOAD_RESULT_FILES_ZIP
const getDeleteResultFolderUrl = API.CORE.CALCULATIONS.DELETE_RESULT_FOLDER
const getDownloadZipFileUrl = API.CORE.CALCULATIONS.DOWNLOAD_ZIP_FILE
const getCalculationProgressUrl = API.CORE.CALCULATIONS.GET_CALCULATION_PROGRESS

export const useCalculationStore = defineStore('calculation', () => {
  const authorizationStore = useAuthorizationStore()

  const canAddCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_calculation']
    })
  })

  const canDeleteCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_calculation']
    })
  })

  const canStopCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_calculation', 'delete_calculation']
    })
  })

  const baseStore = useBaseStore<Calculation>({ service: baseUrl, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)

  const resultFiles = ref<CalculationResultFile[]>([])
  const resultStorageCost = ref<CalculationStorageCost | null>(null)
  const resultFilesLoading = ref(false)
  const resultFileDownloadLoading = ref<string | null>(null)
  const resultFilesZipDownloadLoading = ref<string | null>(null)
  const resultFileDeleteLoading = ref<string | null>(null)
  const resultFolderDeleteLoading = ref<string | null>(null)
  const zipFileDownloadLoading = ref<number | null>(null)
  const rescheduleLoading = ref<number | null>(null)
  const stopLoading = ref<number | null>(null)
  const computeResources = ref<CalculationComputeResource[]>([])
  const historicalComputeResources = ref<CalculationComputeResource[]>([])
  const computeResourcesLoading = ref(false)
  const historicalComputeResourcesLoading = ref(false)
  const computeResourceLogsLoading = ref(false)
  const computeResourceLogDownloadLoading = ref<string | null>(null)
  const containerImages = ref<ContainerImageOption[]>([])
  const containerImagesLoading = ref(false)
  const containerImagesError = ref<string | null>(null)

  const filterFields = ref(['name', 'note'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const createNewCalculation = (project: number) => {
    const newCalculation = {
      project,
      name: '',
      note: '',
      container_image: '',
      vm_max_runtime_seconds: DEFAULT_VM_MAX_RUNTIME_SECONDS,
      hardware_profile: null,
      favorite: false,
      status: 'DRAFT'
    } as Calculation

    return newCalculation
  }

  const getCalculationFormData = (item: CalculationFormPayload) => {
    const formData = new FormData()

    formData.append('project', item.project.toString())
    formData.append('hardware_profile', item.hardwareProfile.toString())
    formData.append('name', item.name)
    formData.append('note', item.note ?? '')
    formData.append('container_image', item.containerImage ?? '')
    formData.append('vm_max_runtime_seconds', item.vmMaxRuntimeSeconds.toString())

    if (item.zipFile) {
      formData.append('zip_file', item.zipFile)
    }

    return formData
  }

  const saveCalculationForm = async (item: CalculationFormPayload) => {
    try {
      baseStore.writeLoading.value = true
      const formData = getCalculationFormData(item)
      const responseData = await api.service(baseUrl).create(formData)

      if (!responseData) return false

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === responseData.id
      )

      if (index >= 0) {
        baseStore.items.value[index] = responseData
      } else {
        baseStore.addItems([responseData])
      }

      baseStore.setCurrentItem(responseData)
      return responseData
    } catch (e) {
      console.error(e)
      return false
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const celeryCalculate = async (item: Calculation) => {
    try {
      const response = await api.service(getCeleryCalculateUrl(item.id)).post(item)
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const rescheduleCalculation = async (
    item: Calculation,
    payload?: CalculationReschedulePayload
  ) => {
    rescheduleLoading.value = item.id
    try {
      const response = await api.service(getRescheduleUrl(item.id)).post({
        container_image: payload?.containerImage ?? item.container_image ?? '',
        hardware_profile: payload?.hardwareProfile ?? item.hardware_profile,
        vm_max_runtime_seconds:
          payload?.vmMaxRuntimeSeconds ??
          item.vm_max_runtime_seconds ??
          DEFAULT_VM_MAX_RUNTIME_SECONDS
      })
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    } finally {
      rescheduleLoading.value = null
    }
  }

  const stopCalculation = async (item: Calculation) => {
    stopLoading.value = item.id
    try {
      const response = await api.service(getStopUrl(item.id)).post(item)
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    } finally {
      stopLoading.value = null
    }
  }

  const fetchContainerImages = async () => {
    containerImagesLoading.value = true
    containerImagesError.value = null
    try {
      containerImages.value = (await api
        .service(containerImagesUrl)
        .get()) as ContainerImageOption[]
      return containerImages.value
    } catch (e) {
      console.error(e)
      containerImagesError.value = apiErrorMessage(e)
      containerImages.value = []
      return []
    } finally {
      containerImagesLoading.value = false
    }
  }

  const apiErrorMessage = (error: unknown) => {
    const candidate = error as {
      message?: string
      response?: {
        data?: {
          detail?: string
        }
      }
    }

    return (
      candidate?.response?.data?.detail ||
      candidate?.message ||
      'Could not load calculation images.'
    )
  }

  const getCalculationProgress = async (
    id: number
  ): Promise<CalculationProgressInfo | undefined> => {
    try {
      return await api.service(getCalculationProgressUrl(id)).get()
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  const getComputeResources = async (id: number): Promise<CalculationComputeResource[]> => {
    computeResourcesLoading.value = true
    try {
      computeResources.value = (await api
        .service(getComputeResourcesUrl(id))
        .get()) as CalculationComputeResource[]
      return computeResources.value
    } catch (e) {
      console.error(e)
      computeResources.value = []
      return []
    } finally {
      computeResourcesLoading.value = false
    }
  }

  const getHistoricalComputeResources = async (
    id: number
  ): Promise<CalculationComputeResource[]> => {
    historicalComputeResourcesLoading.value = true
    try {
      historicalComputeResources.value = (await api
        .service(getHistoricalComputeResourcesUrl(id))
        .get()) as CalculationComputeResource[]
      return historicalComputeResources.value
    } catch (e) {
      console.error(e)
      historicalComputeResources.value = []
      return []
    } finally {
      historicalComputeResourcesLoading.value = false
    }
  }

  const getResultFiles = async (id: number): Promise<CalculationResultFile[]> => {
    resultFilesLoading.value = true
    try {
      const response = (await api
        .service(getResultFilesUrl(id))
        .get()) as CalculationResultFilesResponse
      resultFiles.value = response?.files ?? []
      resultStorageCost.value = response?.storage_cost ?? null
      return resultFiles.value
    } catch (e) {
      console.error(e)
      resultFiles.value = []
      resultStorageCost.value = null
      return []
    } finally {
      resultFilesLoading.value = false
    }
  }

  const getComputeResourceLogs = async (
    calculationId: number,
    computeResourceName: string,
    tail = 500
  ): Promise<CalculationComputeResourceLogsResponse | null> => {
    computeResourceLogsLoading.value = true
    try {
      return (await api
        .service(getComputeResourceLogsUrl(calculationId, computeResourceName, tail))
        .get()) as CalculationComputeResourceLogsResponse
    } catch (e) {
      console.error(e)
      return null
    } finally {
      computeResourceLogsLoading.value = false
    }
  }

  const downloadComputeResourceLogs = async (
    calculationId: number,
    computeResourceName: string
  ) => {
    computeResourceLogDownloadLoading.value = computeResourceName
    try {
      await downloadBlobWithFilename(
        getDownloadComputeResourceLogsUrl(calculationId, computeResourceName),
        `${computeResourceName}.log`
      )
    } catch (e) {
      console.error(e)
    } finally {
      computeResourceLogDownloadLoading.value = null
    }
  }

  const downloadResultFile = async (calculationId: number, file: CalculationResultFile) => {
    resultFileDownloadLoading.value = file.blob_name
    try {
      await downloadBlobWithFilename(
        getDownloadResultFileUrl(calculationId, file.blob_name),
        file.name.split('/').pop() || 'download'
      )
    } catch (e) {
      console.error(e)
    } finally {
      resultFileDownloadLoading.value = null
    }
  }

  const downloadResultFilesZip = async (calculationId: number, pathParts: string[] = []) => {
    const path = pathParts.join('/')
    resultFilesZipDownloadLoading.value = path || '__root__'
    try {
      const calculationName =
        baseStore.currentItem.value?.name?.trim() || `calculation-${calculationId}`
      const zipName = pathParts[pathParts.length - 1] || `${calculationName}-output`
      await downloadBlobWithFilename(
        getDownloadResultFilesZipUrl(calculationId, path),
        zipName.toLowerCase().endsWith('.zip') ? zipName : `${zipName}.zip`
      )
    } catch (e) {
      console.error(e)
    } finally {
      resultFilesZipDownloadLoading.value = null
    }
  }

  const deleteResultFile = async (calculationId: number, file: CalculationResultFile) => {
    resultFileDeleteLoading.value = file.blob_name
    try {
      await api.apiService.api(getDeleteResultFileUrl(calculationId, file.blob_name), 'DELETE')
      await getResultFiles(calculationId)
      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      resultFileDeleteLoading.value = null
    }
  }

  const deleteResultFolder = async (calculationId: number, pathParts: string[]) => {
    const path = pathParts.join('/')
    resultFolderDeleteLoading.value = path || '__root__'
    try {
      await api.apiService.api(getDeleteResultFolderUrl(calculationId, path), 'DELETE')
      await getResultFiles(calculationId)
      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      resultFolderDeleteLoading.value = null
    }
  }

  const downloadZipFile = async (calculation: Calculation) => {
    zipFileDownloadLoading.value = calculation.id
    try {
      const filename =
        calculation.zip_file_name ||
        `${calculation.name?.trim() || `calculation-${calculation.id}`}.zip`
      await downloadBlobWithFilename(getDownloadZipFileUrl(calculation.id), filename)
    } catch (e) {
      console.error(e)
    } finally {
      zipFileDownloadLoading.value = null
    }
  }

  const moveSelectedItemsToArchive = () => {
    console.log('move-selected-items-to-archive')
  }

  const deleteSelectedItems = () => {
    baseStore.openDeleteDialog()
  }

  const downloadBlobWithFilename = async (url: string, filename: string) => {
    const blob = (await silentApi.service(url).getBlob()) as Blob
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(objectUrl)
  }

  function resetAll() {
    baseStore.reset()
    resultFiles.value = []
    resultStorageCost.value = null
    resultFilesLoading.value = false
    resultFileDownloadLoading.value = null
    resultFilesZipDownloadLoading.value = null
    resultFileDeleteLoading.value = null
    resultFolderDeleteLoading.value = null
    zipFileDownloadLoading.value = null
    rescheduleLoading.value = null
    stopLoading.value = null
    computeResources.value = []
    historicalComputeResources.value = []
    computeResourcesLoading.value = false
    historicalComputeResourcesLoading.value = false
    computeResourceLogsLoading.value = false
    computeResourceLogDownloadLoading.value = null
    containerImages.value = []
    containerImagesLoading.value = false
    containerImagesError.value = null
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    canAddCalculation,
    canDeleteCalculation,
    canStopCalculation,
    fieldsFiltered,
    createNewCalculation,
    saveCalculationForm,
    celeryCalculate,
    rescheduleCalculation,
    stopCalculation,
    resultFiles,
    resultStorageCost,
    resultFilesLoading,
    resultFileDownloadLoading,
    resultFilesZipDownloadLoading,
    resultFileDeleteLoading,
    resultFolderDeleteLoading,
    zipFileDownloadLoading,
    rescheduleLoading,
    stopLoading,
    computeResources,
    historicalComputeResources,
    computeResourcesLoading,
    historicalComputeResourcesLoading,
    computeResourceLogsLoading,
    computeResourceLogDownloadLoading,
    containerImages,
    containerImagesLoading,
    containerImagesError,
    fetchContainerImages,
    getCalculationProgress,
    getComputeResources,
    getHistoricalComputeResources,
    getResultFiles,
    getComputeResourceLogs,
    downloadComputeResourceLogs,
    downloadResultFile,
    downloadResultFilesZip,
    deleteResultFile,
    deleteResultFolder,
    downloadZipFile,
    moveSelectedItemsToArchive,
    deleteSelectedItems,
    resetAll
  }
})
